import { ColDef } from '@ag-grid-community/core';
import { ClientWalletRoleEnum, TransactionStatusCode } from '@tassat/common-definitions';
import { UserAccountRecord, UserRecord, UserRole } from '@tassat/fred-definitions';
import { useCallback, useContext, useMemo } from 'react';

import { useAppSelector } from 'Reducers/store/hooks';
import { getUser } from 'Selectors/common';
import { PledgeAction } from 'SharedComponents/actionsDropdownMenu/pledgeActionsDropdownMenu';
import PledgeContextWorkflowContext from 'SharedComponents/modals/pledgeDetailsModal/pledgeContextWorkflow/pledgeContextWorkflowContext';
import { ModalScreen } from 'SharedComponents/modals/pledgeDetailsModal/pledgeDetailsModal';

import { getColumnDef } from './gridOptions';

export const usePledgeTransactionColumnDefs = (opts: {
    currentUser: UserRecord;
    currentRole: UserRole;
}): ColDef[] => {
    const { currentUser, currentRole } = opts;
    const { dispatchPledgeContextWorkflowAction } = useContext(PledgeContextWorkflowContext)!;
    const user = useAppSelector(getUser);

    const getActions = useCallback((data: any, userInitiatingAccount: UserAccountRecord | undefined): PledgeAction[] => {
        if (
            currentUser.IsAdmin ||
            data.PledgeTransactionStatus !== TransactionStatusCode.PENDING_APPROVAL ||
            !userInitiatingAccount ||
            userInitiatingAccount.ClientRoleId === ClientWalletRoleEnum.VIEW_ONLY
        ) {
            return [];
        }

        const isApprover = userInitiatingAccount?.ClientRoleId === ClientWalletRoleEnum.APPROVER;
        const isRequester = user.UserId === data.PledgeTransactionRequesterId as any;

        const saveRowAndNavigate = (screen: ModalScreen) => {
            dispatchPledgeContextWorkflowAction({
                type: 'SET_SELECTED_PLEDGE_TRANSACTION',
                payload: data
            });
            dispatchPledgeContextWorkflowAction({
                type: 'CHANGE_MODAL_SCREEN',
                payload: { screen: screen }
            });
        };

        const cancelAction: PledgeAction = {
            actionText: 'Cancel',
            handleAction: () => saveRowAndNavigate('cancel')
        };

        if (isRequester) {
            return [cancelAction];
        }

        const approveAction: PledgeAction = {
            actionText: 'Approve',
            handleAction: () => saveRowAndNavigate('approve')
        };

        const declineAction: PledgeAction = {
            actionText: 'Decline',
            handleAction: () => saveRowAndNavigate('decline')
        };

        if (isApprover) {
            return [ approveAction, declineAction ];
        }

        return [];
    }, [ currentUser.IsAdmin, dispatchPledgeContextWorkflowAction, user.UserId ]);

    return useMemo(() => {
        const baseColumns = getColumnDef({
            currentUser,
            currentRole
        });

        if (currentUser.IsAdmin) {
            return baseColumns;
        }

        return [
            ...baseColumns,
            {
                field: 'actions',
                headerName: 'Actions',
                cellRenderer: 'pledgeActionButtonCellRenderer',
                cellRendererParams: { getActions },
                sortable: false,
                filter: false,
                width: 100,
                menuTabs: []
            }
        ];
    }, [ currentUser, currentRole, getActions ]);
};